import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { GitMerge, Monitor, Cloud } from "lucide-react";
import { OfflineEntry } from "@/lib/offline/db";
import { useOffline } from "@/contexts/OfflineContext";

interface Props {
  entry: OfflineEntry | null;
  open: boolean;
  onClose: () => void;
}

export function ConflictResolutionDialog({ entry, open, onClose }: Props) {
  const { resolveConflict } = useOffline();
  const [resolution, setResolution] = useState<"local" | "cloud" | "merge">("local");
  const [resolving, setResolving] = useState(false);

  if (!entry || !entry.conflictData) return null;

  const handleResolve = async () => {
    setResolving(true);
    await resolveConflict(entry.id, resolution);
    setResolving(false);
    onClose();
  };

  const renderData = (data: Record<string, unknown>) => (
    <pre className="text-xs bg-muted/50 rounded-lg p-3 overflow-auto max-h-40 border border-border">
      {JSON.stringify(data, null, 2)}
    </pre>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-amber-500" />
            Resolve Sync Conflict
          </DialogTitle>
          <DialogDescription>
            This {entry.type} entry was modified both locally and in the cloud. Choose how to resolve.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Monitor className="h-4 w-4 text-primary" />
              <span className="font-semibold text-sm">Local Version</span>
              <Badge variant="outline" className="text-[10px]">Your Device</Badge>
            </div>
            {renderData(entry.conflictData.localVersion)}
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Cloud className="h-4 w-4 text-blue-500" />
              <span className="font-semibold text-sm">Cloud Version</span>
              <Badge variant="outline" className="text-[10px]">Server</Badge>
            </div>
            {renderData(entry.conflictData.cloudVersion)}
          </div>
        </div>

        <RadioGroup value={resolution} onValueChange={(v) => setResolution(v as "local" | "cloud" | "merge")} className="space-y-3 mt-2">
          <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
            <RadioGroupItem value="local" id="local" className="mt-0.5" />
            <Label htmlFor="local" className="cursor-pointer">
              <div className="font-semibold text-sm">Keep Local Version</div>
              <div className="text-xs text-muted-foreground">Overwrite cloud data with your local changes</div>
            </Label>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
            <RadioGroupItem value="cloud" id="cloud" className="mt-0.5" />
            <Label htmlFor="cloud" className="cursor-pointer">
              <div className="font-semibold text-sm">Keep Cloud Version</div>
              <div className="text-xs text-muted-foreground">Discard local changes and use server data</div>
            </Label>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
            <RadioGroupItem value="merge" id="merge" className="mt-0.5" />
            <Label htmlFor="merge" className="cursor-pointer">
              <div className="font-semibold text-sm">Merge Changes</div>
              <div className="text-xs text-muted-foreground">Combine both versions (local fields take priority)</div>
            </Label>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleResolve} disabled={resolving}>
            {resolving ? "Resolving..." : "Apply Resolution"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
